// EmployeeDrawer.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Drawer,
  Typography,
  Box,
  Card,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Switch,
  FormControlLabel,
  useTheme,
  Button,
  ToggleButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { debounce, sortBy } from "lodash";
import EmployeeAccordion from "components/EmployeeAccordion";
import SnackbarWrapper from "components/Snackbar";
import { useTranslation } from "react-i18next";
import { addManpowerRequirementsEmployee } from "services/api";
import { Add, BarChart } from "@mui/icons-material";
import MDButton from "components/MDButton";
import { addScenarioEmployee } from "services/api";
import { deleteScenarioEmployee } from "services/api";

const EmployeeDrawer = ({
  open,
  onClose,
  employeeLevel,
  drawerLoading,
  setEmployeeLevel,
  isScenario,
  jobId,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const snackbarRef = useRef();

  const [showAddField, setShowAddField] = useState(false);
  const [newEmployeeName, setNewEmployeeName] = useState("");

  const [sortByScore, setSortByScore] = useState(false);
  const [sortedEmployees, setSortedEmployees] = useState([]);

  const toggleSortByScore = () => {
    if (!employeeLevel || employeeLevel.length === 0) return;

    if (!sortByScore) {
      // Sort mode ON
      const updatedEmployees = employeeLevel.map((emp) => {
        const score = calculateEmployeeScore(emp);
        return { ...emp, score };
      });

      const sorted = [...updatedEmployees].sort((a, b) => a.score - b.score);
      setSortedEmployees(sorted);
      setSortByScore(true);
    } else {
      // Sort mode OFF - restore original order
      setSortedEmployees([]);
      setSortByScore(false);
    }
  };

  const calculateEmployeeScore = (emp) => {
    if (!emp) return 0;

    // --- 2. PIR (Position In Range) ---
    let pirScore = 0;
    if (emp.pir != null) {
      const deviation = Math.abs(emp.pir - 0.65);
      pirScore = Math.max(0, 100 - deviation * 150);
    }

    // --- 3. Age factor ---
    let ageScore = 0;
    if (emp.dateOfBirth) {
      const birthYear = new Date(emp.dateOfBirth).getFullYear();
      const age = new Date().getFullYear() - birthYear;
      if (age >= 20 && age <= 60) {
        ageScore = 100 - (age - 20) * 1.2;
      } else if (age < 20) {
        ageScore = 100;
      } else {
        ageScore = 50;
      }
    }

    // --- 4. Experience ---
    let experienceScore = 0;
    if (emp.experienceYears != null) {
      experienceScore = Math.min(emp.experienceYears * 5, 100);
    }

    // --- 5. Competency ---
    const competencyScore = Math.min(emp.competencyScore ?? 0, 100);

    // --- 6. Credentials ---
    const credentialsScore = Math.min(emp.credentialsScore ?? 0, 100);

    // --- 7. Performance Score ---
    const performanceScore = Math.min(emp.performanceScore ?? 0, 100);

    // --- 8. Privilege Level ---
    let privilegeScore = 0;
    if (emp.privilegesLevel != null) {
      privilegeScore = Math.min((emp.privilegesLevel / 10) * 100, 100);
    }

    // --- 9. Salary factor (negative impact) ---
    let salaryPenalty = 0;
    if (emp.salary != null) {
      const normalized = Math.min(emp.salary / 100000, 1);
      salaryPenalty = normalized * 15;
    }

    // --- 10. Weighted Final Calculation ---
    let totalScore =
      performanceScore * 0.3 + // more weight
      competencyScore * 0.2 +
      credentialsScore * 0.15 +
      experienceScore * 0.1 +
      pirScore * 0.1 +
      ageScore * 0.05 +
      privilegeScore * 0.1 - // stronger impact
      salaryPenalty;

    // --- 11. Talent Bonus (10%) ---
    if (emp.talented) {
      totalScore *= 1.1;
    }

    // --- 12. Cap to 100 max ---
    totalScore = Math.min(totalScore, 100);

    return Number(totalScore.toFixed(2));
  };

  const deleteEmployee = async (employeeId) => {
    const res = await deleteScenarioEmployee(employeeId);
    if (res != "error") {
      setEmployeeLevel(employeeLevel.filter((e) => e.employeeId != employeeId));
    }
  };

  const handleSaveEmployee = async (e) => {
    if (!newEmployeeName.trim()) return;

    const trimmedName = newEmployeeName.trim();
    if (!trimmedName) return;

    const nameExists = employeeLevel.some(
      (emp) => emp.employeeName?.toLowerCase() === trimmedName.toLowerCase()
    );

    if (nameExists) {
      snackbarRef.current.showSnackbar(t("user.employeeDrawer.doesntExist"), "error");
      return;
    }

    // 🧠 Call API or handle locally
    const res = await addScenarioEmployee({ employeeName: trimmedName, jobId });
    if (res != "error") {
      setEmployeeLevel((prev) => [...prev, res]);
    }
    setNewEmployeeName("");
    setShowAddField(false);
  };

  // Expand/collapse handler
  const handleExpand = (id) => {
    setExpanded(expanded === id ? false : id);
  };

  const handleUpdateEmployeeData = async (id, updated) => {
    try {
      // 1️⃣ Find the employee to update
      const emp = employeeLevel.find((e) => e.employeeId === id);
      if (!emp) return;

      // 2️⃣ Optimistically update UI (instant feedback)
      setEmployeeLevel((prev) => prev.map((e) => (e.employeeId === id ? { ...e, ...updated } : e)));

      // 3️⃣ Send update to backend
      const payload = { ...emp, ...updated };
      if (isScenario) {
        await addScenarioEmployee(payload);
      } else {
        await addManpowerRequirementsEmployee(payload);
      }
      snackbarRef.current?.showSnackbar(t("user.employeeDrawer.successfully"), "success");
    } catch (error) {
      snackbarRef.current?.showSnackbar(t("user.employeeDrawer.error"), "error");
    }
  };

  // Handle input change
  const handleChange = (id, field, value) => {
    handleUpdateEmployeeData(id, { [field]: value });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "90%", sm: 400 },
          p: 2,
          backgroundColor: "#fafafa",
          borderLeft: `2px solid ${theme.palette.primary.main}`,
          overflowY: "auto",
        },
        onClick: (e) => e.stopPropagation(),
      }}
    >
      {/* Title Row */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6" sx={{ color: "primary.main" }}>
          {t("user.employeeDrawer.employees")}
        </Typography>
        {!drawerLoading && (
          <ToggleButton
            value="sort"
            selected={sortByScore}
            onChange={(e, toggled) => {
              e.stopPropagation();
              toggleSortByScore();
            }}
            sx={{
              height: 30,
              textTransform: "none",
              borderRadius: 2,
              borderColor: sortByScore ? "success.main" : "info.main",
              color: sortByScore ? "success.main" : "info.main",
              "&.Mui-selected": {
                bgcolor: "success.main",
                color: "white",
                "&:hover": {
                  bgcolor: "success.dark",
                },
              },
            }}
          >
            <BarChart sx={{ fontSize: 18, mr: 0.5 }} />
            {t("user.employeeDrawer.sortByScore")}
          </ToggleButton>
        )}
      </Box>

      {isScenario && !drawerLoading && (
        <Box sx={{ mb: 2 }}>
          {!showAddField ? (
            <MDButton
              color="primary"
              variant="outlined"
              startIcon={<Add />}
              onClick={(e) => {
                e.stopPropagation();
                setShowAddField(true);
              }}
              sx={{ mb: 1 }}
              fullWidth
            >
              {t("user.scenarios.addEmp")}
            </MDButton>
          ) : (
            <Box display="flex" gap={1}>
              <TextField
                size="small"
                variant="outlined"
                label={t("user.employeeDrawer.addEmployee")}
                value={newEmployeeName}
                onChange={(e) => setNewEmployeeName(e.target.value)}
                fullWidth
              />
              <MDButton
                color="success"
                variant="contained"
                onClick={handleSaveEmployee}
                disabled={!newEmployeeName.trim()}
              >
                {t("user.employeeDrawer.save")}
              </MDButton>
              <MDButton
                variant="text"
                color="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAddField(false);
                }}
              >
                {t("user.employeeDrawer.cancel")}
              </MDButton>
            </Box>
          )}
        </Box>
      )}

      {/* Main Content */}
      {drawerLoading ? (
        <Box display="flex" justifyContent="center" p={1}>
          <CircularProgress size={20} sx={{ color: "black" }} />
        </Box>
      ) : !sortByScore ? (
        employeeLevel && employeeLevel.length > 0 ? (
          employeeLevel.map((emp, index) => (
            <EmployeeAccordion
              key={emp.employeeId}
              emp={emp}
              expanded={expanded}
              handleExpand={handleExpand}
              handleChange={handleChange}
              isScenario={isScenario}
              handleDeleteEmployee={deleteEmployee}
              isSorted={sortByScore} // 👈 send sorting toggle
              placement={index + 1}
            />
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t("user.employeeDrawer.NoEmployees")}
          </Typography>
        )
      ) : sortedEmployees && sortedEmployees.length > 0 ? (
        sortedEmployees.map((emp, index) => (
          <EmployeeAccordion
            key={emp.employeeId}
            emp={emp}
            expanded={expanded}
            handleExpand={handleExpand}
            handleChange={handleChange}
            isScenario={isScenario}
            handleDeleteEmployee={deleteEmployee}
            isSorted={sortByScore} // 👈 send sorting toggle
            placement={index + 1}
          />
        ))
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t("user.employeeDrawer.NoEmployees")}
        </Typography>
      )}

      <SnackbarWrapper
        ref={snackbarRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Drawer>
  );
};

// ✅ PropTypes
EmployeeDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  employeeLevel: PropTypes.array.isRequired,
  drawerLoading: PropTypes.bool.isRequired,
  setEmployeeLevel: PropTypes.func.isRequired,
  isScenario: PropTypes.bool.isRequired,
  jobId: PropTypes.number.isRequired,
  addNumberOfEmployees: PropTypes.func,
  handleDeleteEmployee: PropTypes.func,
};

// Default props (optional)
EmployeeDrawer.defaultProps = {
  employeeLevel: [],
  drawerLoading: false,
};

export default EmployeeDrawer;
